import { StamdataMaalbeskrivelseApi } from "services/api/stamdataMaalbeskrivelse/stamdataMaalbeskrivelseApi";
import { StamdataSpecialeApi } from "services/api/stamdataSpeciale/stamdataSpecialeApi";
import { StamdataStillingApi } from "services/api/stamdataStilling/stamdataStillingApi";
import { LogbogUserInfoApi } from "services/api/logbogUser/logbogUserInfoApi";
import { PdfKompetenceHeaderModel } from "../models/pdfKompetenceHeaderModel";
import { PdfKursusHeaderModel } from "../models/pdfKursusHeaderModel";
import { PdfBeskaeftigelseHeaderModel } from "../models/pdfBeskaeftigelseHeaderModel";

export type KompetenceHeaderApis = {
    stamdataMaalbeskrivelseApi: StamdataMaalbeskrivelseApi;
    stamdataSpecialeApi: StamdataSpecialeApi;
    logbogUserInfoApi: LogbogUserInfoApi;
    stamdataStillingApi?: StamdataStillingApi;
};

export type KompetenceHeaderWithStillingApis = KompetenceHeaderApis & {
    stamdataStillingApi: StamdataStillingApi;
};

export async function fetchKompetenceHeader(
    brugerId: string,
    brugerMaalbeskrivelseId: string,
    apis: KompetenceHeaderApis,
    stillingInfo?: {stillingId: string, forloebId: string},
): Promise<PdfKompetenceHeaderModel> {

    const brugerMaalbeskrivelse = await apis.stamdataMaalbeskrivelseApi.getBrugerMaalbeskrivelse(brugerMaalbeskrivelseId);
    const speciale = await apis.stamdataSpecialeApi.getSpecialeByBrugerMaalbeskrivelse(brugerMaalbeskrivelseId);
    const bruger = await apis.logbogUserInfoApi.getUserInfoById(brugerId);

    if(!stillingInfo || !apis.stamdataStillingApi) {
        return {
            brugerMaalbeskrivelse,
            speciale,
            bruger
        }};

    const stilling = await apis.stamdataStillingApi.getStillingDisplayInfo(stillingInfo.stillingId, stillingInfo.forloebId);

    return {
        brugerMaalbeskrivelse,
        speciale,
        stilling,
        bruger
    };
}

export async function fetchKompetenceHeadersMap(
    brugerId: string,
    brugerMaalbeskrivelseIds: string[],
    apis: KompetenceHeaderApis,
): Promise<Map<string, PdfKompetenceHeaderModel>> {
    const entries = await Promise.all(
        brugerMaalbeskrivelseIds.map(async (id) => [
            id,
            await fetchKompetenceHeader(brugerId, id, apis),
        ] as [string, PdfKompetenceHeaderModel])
    );
    return new Map(entries);
}

export async function fetchKursusHeader(
    brugerId: string,
    brugerMaalbeskrivelseId: string,
    apis: { stamdataSpecialeApi: StamdataSpecialeApi; logbogUserInfoApi: LogbogUserInfoApi }
): Promise<PdfKursusHeaderModel> {
    const [speciale, bruger] = await Promise.all([
        apis.stamdataSpecialeApi.getSpecialeByBrugerMaalbeskrivelse(brugerMaalbeskrivelseId),
        apis.logbogUserInfoApi.getUserInfoById(brugerId),
    ]);
    return { speciale, bruger };
}

export async function fetchBeskaeftigelseHeader(
    brugerId: string,
    brugerMaalbeskrivelseId: string,
    apis: KompetenceHeaderApis
): Promise<PdfBeskaeftigelseHeaderModel> {
    const [brugerMaalbeskrivelse, speciale, bruger] = await Promise.all([
        apis.stamdataMaalbeskrivelseApi.getBrugerMaalbeskrivelse(brugerMaalbeskrivelseId),
        apis.stamdataSpecialeApi.getSpecialeByBrugerMaalbeskrivelse(brugerMaalbeskrivelseId),
        apis.logbogUserInfoApi.getUserInfoById(brugerId),
    ]);
    return { speciale, bruger, maalbeskrivelseNavn: brugerMaalbeskrivelse.navn };
}
